/**
 * Просмотр договора, ТЗ и обоснования НМЦК в приложении (UI-3, поверх Х-3): какие документы
 * карточки открываются, группы переключателя и документ по цитате раскладки. Без DOM —
 * тестируется в node.
 */

/** Группы переключателя окна; календарный план хранится вместе с договором (Х-3). */
export const VIEW_GROUPS = [
  { key: 'contract', label: 'Договор', kinds: ['contract', 'calendar_plan'] },
  { key: 'tz', label: 'ТЗ', kinds: ['tz'] },
  { key: 'nmck', label: 'НМЦК', kinds: ['nmck'] },
];

/** Тип документа в шапке окна. */
export const VIEW_KIND_LABEL = {
  contract: 'Договор', calendar_plan: 'Календарный план', tz: 'ТЗ', nmck: 'Обоснование НМЦК',
};

export const ERASED_HINT = 'файл удалён по окончании срока подачи, оригинал в ЕИС';

const groupOf = (kind) => VIEW_GROUPS.find((g) => g.kinds.includes(kind))?.key ?? null;

/** Документ открывается в окне: хранимый тип, файл есть и /view знает, что отдать. */
export const isViewable = (d) => !!d && !!groupOf(d.doc_kind) && !!d.has_file && !!d.view_kind;

/** Файл хранимого типа стёрт housekeeping'ом по сроку подачи (note из Х-3). */
export const isErased = (d) => !!d && !!groupOf(d.doc_kind) && !d.has_file && /^файл удалён/.test(d.note ?? '');

/** Документы окна по группам: { contract: [...], tz: [...], nmck: [...] } в порядке карточки. */
export function viewGroups(t) {
  const out = Object.fromEntries(VIEW_GROUPS.map((g) => [g.key, []]));
  for (const d of t?.documents ?? []) if (isViewable(d)) out[groupOf(d.doc_kind)].push(d);
  return out;
}

export const viewGroupKey = (d) => groupOf(d?.doc_kind);

/** «DOCX» из имени файла; pdf без расширения — по view_kind. */
export function fileFormat(d) {
  const m = /\.([a-z0-9]{2,5})$/i.exec(d?.filename ?? '');
  if (m) return m[1].toUpperCase();
  return d?.view_kind === 'pdf' ? 'PDF' : '';
}

const norm = (s) => String(s ?? '').toLowerCase().replace(/ё/g, 'е').replace(/[^a-zа-я0-9]+/g, ' ').trim();
const stem = (name) => norm(String(name ?? '').replace(/\.[a-z0-9]{2,5}$/i, ''));

// ТЗ раньше договора: «Техническое задание (приложение к контракту)» — это ТЗ.
const KIND_WORDS = [
  ['tz', /(^|\s)тз(\s|$)|техническ|задани/],
  ['nmck', /(нмц|смет|обоснован)/],
  ['contract', /(договор|контракт|календарн)/],
];

/**
 * Документ окна по цитате раскладки («документ, пункт» строкой или {document, clause}):
 * сначала по имени файла, затем по словам «договор», «ТЗ», «смета» — первый документ группы.
 * null — цитата на карточку закупки или документа нет.
 */
export function docForCitation(t, citation) {
  const text = typeof citation === 'object' && citation ? citation.document : citation;
  const c = norm(text);
  if (!c) return null;
  const docs = (t?.documents ?? []).filter(isViewable);
  if (!docs.length) return null;
  const byName = docs
    .map((d) => ({ d, s: stem(d.filename) }))
    .filter((x) => x.s.length >= 3 && c.includes(x.s))
    .sort((a, b) => b.s.length - a.s.length)[0];
  if (byName) return byName.d;
  const head = norm(typeof citation === 'object' ? citation.document : String(text).split(',')[0]);
  const kind = KIND_WORDS.find(([, re]) => re.test(head))?.[0];
  if (!kind) return null;
  return viewGroups(t)[kind][0] ?? null;
}
